import client from 'api/apollo-client';
import { getServerPageGetPressReleases } from 'api/graphql-helper';
import { EmptyState } from 'components/empty-state/empty-state';
import { CardNews } from 'components/card';
import { Pagination } from 'components/pagination';
import { usePrevious } from 'utils/use-previous';
import router from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'redux/store';
import { setCurrentPage, setPressReleases, setTotalPressReleases } from 'redux/stores/press-releases';

import styles from './press-release-overview.module.scss';

const LIMIT = 12;

const PressReleaseOverview = (): React.JSX.Element => {
	const { publicationState } = useAppSelector((state) => state.settings);
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const { pressReleases, totalPressReleases, currentPage } = useAppSelector((state) => state.pressRelease);

	const overviewRef = useRef<HTMLDivElement>(null);

	const prevCurrentPage = usePrevious(currentPage);

	useEffect(() => {
		if (currentPage !== prevCurrentPage && prevCurrentPage !== null) {
			getPressReleases(currentPage);
		}
	}, [currentPage]);

	const getPressReleases = async (page: number): Promise<void> => {
		setIsLoading(true);

		const start = (page - 1) * LIMIT;
		const pressReleaseResult = await getServerPageGetPressReleases(
			{
				variables: {
					limit: LIMIT,
					start,
					sort: 'publishedAt:desc',
					locale: router.locale,
					publicationState
				}
			},
			client
		);

		dispatch(setPressReleases(pressReleaseResult.props.data.pressReleases?.data.map((item) => item.attributes)));
		dispatch(setTotalPressReleases(pressReleaseResult.props.data.pressReleases?.meta?.pagination?.total || 0));
		setIsLoading(false);
	};

	const handlePageChange = (page: number): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 100;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.pageYOffset - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}

		dispatch(setCurrentPage(page));
	};

	return (
		<div className={styles.PressReleaseOverview} ref={overviewRef}>
			<div className={styles.wrapper}>
				{(pressReleases?.length || 0) > 0 ? (
					<div
						className={styles.content}
						style={{
							opacity: isLoading ? '0.2' : 1,
							height: pressReleases?.length === LIMIT ? '1350px' : '300px'
						}}
					>
						{pressReleases?.map((article, index) => (
							<div key={index}>
								<CardNews
									id={`press-release-${index}`}
									bodyText={article?.bodyText?.bodyText || ''}
									excerpt={article?.header?.excerpt || ''}
									publishedAt={article?.publishedAt}
									slug={article?.slug || ''}
									title={article?.title || ''}
									key={index}
									hideCover
									type="persberichten"
									priority={index < 3}
								/>
							</div>
						))}
					</div>
				) : (
					<div className={styles.emptyState}>
						<EmptyState onClearFilters={() => {}} isLoading={isLoading} />
					</div>
				)}
			</div>
			<div className={styles.pagination}>
				<Pagination
					currentPage={currentPage}
					totalPages={Math.ceil(totalPressReleases / LIMIT)}
					onPageChange={handlePageChange}
				/>
			</div>
		</div>
	);
};

export default PressReleaseOverview;
